import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './DataRoom.css';

const DataRoom = ({ userInfo }) => {
  const [allFiles, setAllFiles] = useState([]);
  const [investorTeam, setInvestorTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [activeTab, setActiveTab] = useState('documents');

  // Investor team member states
  const [ndaRecords, setNdaRecords] = useState([]);
  const [ndaLoading, setNdaLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    title: '',
    bio: '',
    headshot: null
  });

  // Bio popup state
  const [bioPopupMember, setBioPopupMember] = useState(null);

  // NDA search state
  const [ndaSearch, setNdaSearch] = useState('');

  // Custom categories state
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryVisible, setNewCategoryVisible] = useState(false);
  const [newCategoryConfidential, setNewCategoryConfidential] = useState(true);

  const canEdit = userInfo?.accountType === 'admin' ||
                  userInfo?.accountType === 'superadmin' ||
                  userInfo?.accountType === 'client' ||
                  userInfo?.accountType === 'super_user' ||
                  userInfo?.accountType === 'super_client' ||
                  userInfo?.accountType === 'founder';

  const isAdmin = userInfo?.accountType === 'admin' || userInfo?.accountType === 'superadmin';

  // Hardcoded document sections (data room + investor)
  const hardcodedSections = [
    { id: 'one-pager', name: 'Company One-Pager', category: 'One-Pager', defaultVisibleToInvestors: true, defaultConfidential: false },
    { id: 'slide-deck', name: 'Nonconfidential Slide Deck', category: 'Slide Deck', defaultVisibleToInvestors: true, defaultConfidential: false },
    { id: 'executive-summary', name: 'Executive Summary', category: 'Executive Summary', defaultVisibleToInvestors: false, defaultConfidential: true },
    { id: 'pitch-deck', name: 'Pitch Deck', category: 'Pitch Deck', defaultVisibleToInvestors: false, defaultConfidential: true },
    { id: 'preliminary-data', name: 'Preliminary Data', category: 'Preliminary Data', defaultVisibleToInvestors: false, defaultConfidential: true },
    { id: 'pharmacology-toxicology', name: 'Pharmacology & Toxicology Data', category: 'Pharmacology & Toxicology', defaultVisibleToInvestors: false, defaultConfidential: true },
    { id: 'cmc', name: 'Chemistry, Manufacturing, and Controls (CMC)', category: 'CMC', defaultVisibleToInvestors: false, defaultConfidential: true },
  ];

  // Merge hardcoded + custom categories
  const documentSections = [
    ...hardcodedSections,
    ...customCategories.map(c => ({
      id: c.id,
      name: c.name,
      category: c.category || c.name,
      defaultVisibleToInvestors: c.defaultVisibleToInvestors === 'true',
      defaultConfidential: c.defaultConfidential === 'true',
    }))
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      try {
        const filesResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/all`
        );
        if (filesResponse.ok) {
          const filesData = await filesResponse.json();
          setAllFiles(filesData);
        }
      } catch (e) {
        console.error('Error fetching data room files:', e);
      }

      try {
        const investorResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/data`
        );
        if (investorResponse.ok) {
          const data = await investorResponse.json();
          setInvestorTeam(data.teamMembers || []);
        }
      } catch (e) {
        console.error('Error fetching investor data:', e);
      }

      try {
        const catResponse = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/data-room/categories`
        );
        if (catResponse.ok) {
          const catData = await catResponse.json();
          setCustomCategories(catData);
        }
      } catch (e) {
        console.error('Error fetching custom categories:', e);
      }

      try {
        const overrideResp = await api.get(
          `${process.env.REACT_APP_BACKEND_API_URL}/data-room/hardcoded-defaults`
        );
        if (overrideResp.ok) {
          const rows = await overrideResp.json();
          const map = {};
          (Array.isArray(rows) ? rows : []).forEach(r => {
            map[r.id] = {
              defaultVisibleToInvestors: r.defaultVisibleToInvestors === 'true',
              defaultConfidential: r.defaultConfidential === 'true',
            };
          });
          setCategoryDefaultOverrides(map);
        }
      } catch (e) {
        console.error('Error fetching hardcoded category defaults:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Category name is required');
      return;
    }
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/data-room/categories`,
        {
          name: newCategoryName.trim(),
          defaultVisibleToInvestors: newCategoryVisible,
          defaultConfidential: newCategoryConfidential,
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomCategories(prev => [...prev, data.category]);
        setShowAddCategory(false);
        setNewCategoryName('');
        setNewCategoryVisible(false);
        setNewCategoryConfidential(true);
      } else {
        alert('Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      alert('Failed to create category');
    }
  };

  // State to track local overrides of hardcoded category defaults
  const [categoryDefaultOverrides, setCategoryDefaultOverrides] = useState({});

  const getCategoryDefaults = (section) => {
    const override = categoryDefaultOverrides[section.id];
    if (override) return override;
    return {
      defaultVisibleToInvestors: section.defaultVisibleToInvestors,
      defaultConfidential: section.defaultConfidential,
    };
  };

  const handleUpdateCategoryDefaults = async (section, field, value) => {
    // For custom categories, update via API
    const customCat = customCategories.find(c => c.id === section.id);
    if (customCat) {
      try {
        const response = await api.put(
          `${process.env.REACT_APP_BACKEND_API_URL}/data-room/categories/${section.id}`,
          { [field]: value }
        );
        if (response.ok) {
          setCustomCategories(prev => prev.map(c =>
            c.id === section.id ? { ...c, [field]: String(value) } : c
          ));
        }
      } catch (err) {
        console.error('Error updating category defaults:', err);
      }
    } else {
      // For hardcoded categories, update local state AND persist to backend
      const nextDefaults = { ...getCategoryDefaults(section), [field]: value };
      setCategoryDefaultOverrides(prev => ({
        ...prev,
        [section.id]: nextDefaults,
      }));
      try {
        await api.put(
          `${process.env.REACT_APP_BACKEND_API_URL}/data-room/hardcoded-defaults/${section.id}`,
          { [field]: value }
        );
      } catch (err) {
        console.error('Error persisting hardcoded category defaults:', err);
      }
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleFileUpload = async (category, file, section) => {
    if (!file) return;
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      if (section) {
        const defaults = getCategoryDefaults(section);
        formData.append('defaultVisibleToInvestors', String(defaults.defaultVisibleToInvestors || false));
        formData.append('defaultConfidential', String(defaults.defaultConfidential || false));
      }
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/data-room/upload`,
        formData
      );
      if (response.ok) {
        fetchAllData();
      } else {
        alert('Failed to upload file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${file.id}/download`
      );
      if (!response.ok) {
        alert('Failed to download file');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${fileId}`
      );
      if (response.ok) {
        setAllFiles(prev => prev.filter(f => f.id !== fileId));
      } else {
        alert('Failed to delete file');
      }
    } catch (err) {
      console.error('Error deleting file:', err);
      alert('Failed to delete file');
    }
  };

  const handleVisibilityChange = async (fileId, field, value) => {
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${fileId}/visibility`,
        { [field]: value }
      );
      if (response.ok) {
        setAllFiles(prev =>
          prev.map(f => f.id === fileId ? { ...f, [field]: String(value) } : f)
        );
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const handleAddTeamMember = async () => {
    if (!newMember.name || !newMember.title) {
      alert('Name and title are required');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('name', newMember.name);
      formData.append('title', newMember.title);
      formData.append('bio', newMember.bio);
      if (newMember.headshot) {
        formData.append('headshot', newMember.headshot);
      }
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/team-member`,
        formData
      );
      if (response.ok) {
        const data = await response.json();
        setInvestorTeam(prev => [...prev, data.teamMember]);
        setNewMember({ name: '', title: '', bio: '', headshot: null });
        setShowAddMember(false);
      } else {
        alert('Failed to add team member');
      }
    } catch (err) {
      console.error('Error adding team member:', err);
      alert('Failed to add team member');
    }
  };

  const handleUpdateTeamMember = async (memberId) => {
    if (!editingMember) return;
    try {
      const formData = new FormData();
      formData.append('name', editingMember.name || '');
      formData.append('title', editingMember.title || '');
      formData.append('bio', editingMember.bio || '');
      if (editingMember.newHeadshot) {
        formData.append('headshot', editingMember.newHeadshot);
      }
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/team-member/${memberId}`,
        formData
      );
      if (response.ok) {
        const data = await response.json();
        setInvestorTeam(prev => prev.map(m => m.id === memberId ? data.teamMember : m));
        setEditingMember(null);
      } else {
        alert('Failed to update team member');
      }
    } catch (err) {
      console.error('Error updating team member:', err);
    }
  };

  const handleDeleteTeamMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) return;
    try {
      const response = await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/team-member/${memberId}`
      );
      if (response.ok) {
        setInvestorTeam(prev => prev.filter(m => m.id !== memberId));
      } else {
        alert('Failed to delete team member');
      }
    } catch (err) {
      console.error('Error deleting team member:', err);
    }
  };

  const fetchNdaRecords = async () => {
    try {
      setNdaLoading(true);
      const companyParam = userInfo?.selectedCompany ? `?company=${encodeURIComponent(userInfo.selectedCompany)}` : '';
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/investor-accounts/ndas${companyParam}`
      );
      if (response.ok) {
        const data = await response.json();
        setNdaRecords(data.ndas || data.ndaRecords || []);
      }
    } catch (err) {
      console.error('Error fetching NDA records:', err);
    } finally {
      setNdaLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'nda-access' && ndaRecords.length === 0) {
      fetchNdaRecords();
    }
  };

  // Group files by category
  const getFilesForCategory = (category) => {
    return allFiles.filter(f => f.category === category);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading data room...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Data Room & Investor Portal</h1>
        <p className="text-gray-600 mt-2">Secure repository for company documents, team information, and investor materials</p>
      </div>

      <CompanyOverviewEditor canEdit={canEdit} />


      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('documents')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'documents'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Documents
          </button>
          <button
            onClick={() => handleTabChange('team')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'team'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Team
          </button>
          <button
            onClick={() => handleTabChange('ip')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'ip'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Intellectual Property
          </button>
          <button
            onClick={() => handleTabChange('funding')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'funding'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Funding
          </button>
          <button
            onClick={() => handleTabChange('nda-access')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'nda-access'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            NDA Access
          </button>
        </nav>
      </div>

      {activeTab === 'ip' && <IPTab />}
      {activeTab === 'funding' && <FundingTab />}

      {/* Documents Tab - Collapsible Sections */}
      {activeTab === 'documents' && (
        <div className="space-y-3">
          {isAdmin && (
            <div className="flex justify-end mb-2">
              <button
                onClick={() => setShowAddCategory(true)}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Add Category
              </button>
            </div>
          )}
        <div className="space-y-3">
          {documentSections.map(section => {
            const sectionFiles = getFilesForCategory(section.category);
            const isExpanded = expandedSections[section.id];

            return (
              <div key={section.id} className="card overflow-hidden">
                {/* Section Header */}
                <div
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleSection(section.id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm">{isExpanded ? '▼' : '▶'}</span>
                    <h3 className="text-lg font-semibold text-gray-900">{section.name}</h3>
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                      {sectionFiles.length} file{sectionFiles.length !== 1 ? 's' : ''}
                    </span>
                    {(() => {
                      const confidentialCount = sectionFiles.filter(f => f.confidential === 'true').length;
                      return confidentialCount > 0 ? (
                        <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full font-medium">
                          {confidentialCount} confidential
                        </span>
                      ) : null;
                    })()}
                    {!isExpanded && (() => {
                      const investorVisibleCount = sectionFiles.filter(f => f.visibleToInvestors === 'true').length;
                      return investorVisibleCount > 0 ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                          {investorVisibleCount} visible to investors
                        </span>
                      ) : null;
                    })()}
                  </div>
                  {canEdit && (
                    <label
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Upload
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => {
                          handleFileUpload(section.category, e.target.files[0], section);
                        }}
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>

                {/* Expanded Section Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200">
                    {/* Category Default Settings - Admin Only */}
                    {isAdmin && (
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-4 text-xs">
                        <span className="text-gray-500 font-medium">Category Defaults:</span>
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            checked={getCategoryDefaults(section).defaultVisibleToInvestors}
                            onChange={(e) => handleUpdateCategoryDefaults(section, 'defaultVisibleToInvestors', e.target.checked)}
                          />
                          <span className="text-gray-600">Visible to Investors</span>
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                            checked={getCategoryDefaults(section).defaultConfidential}
                            onChange={(e) => handleUpdateCategoryDefaults(section, 'defaultConfidential', e.target.checked)}
                          />
                          <span className="text-gray-600">Confidential</span>
                        </label>
                      </div>
                    )}

                    {sectionFiles.length === 0 ? (
                      <div className="px-4 py-6 text-center">
                        <p className="text-sm text-gray-500">No files uploaded in this section yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Uploaded</th>
                              {isAdmin && (
                                <>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Visible to Investors</th>
                                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Confidential</th>
                                </>
                              )}
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {sectionFiles.map(file => (
                              <tr key={file.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm text-gray-900">
                                  <div className="flex items-center gap-2">
                                    <span>{file.originalName}</span>
                                    {file.confidential === 'true' && (
                                      <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded font-medium">Confidential</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500">
                                  {file.createdAt ? new Date(file.createdAt).toLocaleDateString() : 'N/A'}
                                </td>
                                {isAdmin && (
                                  <>
                                    <td className="px-4 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        checked={file.visibleToInvestors === 'true'}
                                        onChange={(e) => handleVisibilityChange(file.id, 'visibleToInvestors', e.target.checked)}
                                      />
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                        checked={file.confidential === 'true'}
                                        onChange={(e) => handleVisibilityChange(file.id, 'confidential', e.target.checked)}
                                      />
                                    </td>
                                  </>
                                )}
                                <td className="px-4 py-2 text-center">
                                  <div className="flex items-center justify-center gap-3">
                                    <a
                                      href={`${process.env.REACT_APP_BACKEND_API_URL}/data-room/files/${file.id}/view`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-700 text-sm"
                                    >
                                      View
                                    </a>
                                    <button
                                      onClick={() => handleDownloadFile(file)}
                                      className="text-blue-600 hover:text-blue-700 text-sm"
                                    >
                                      Download
                                    </button>
                                    {canEdit && (
                                      <button
                                        onClick={() => handleDeleteFile(file.id)}
                                        className="text-red-600 hover:text-red-700 text-sm"
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add New Category</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category Name *</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="e.g. Financial Reports"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCategoryVisible}
                    onChange={(e) => setNewCategoryVisible(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">Visible to investors by default</span>
                </label>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newCategoryConfidential}
                    onChange={(e) => setNewCategoryConfidential(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">Confidential by default</span>
                </label>
              </div>
              <div className="flex space-x-2 pt-4">
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1"
                >
                  Create Category
                </button>
                <button
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName('');
                    setNewCategoryVisible(false);
                    setNewCategoryConfidential(true);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Tab */}
      {activeTab === 'team' && (
        <div>
          {canEdit && (
            <div className="mb-6">
              <button
                onClick={() => setShowAddMember(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Team Member
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {investorTeam.filter(m => m && typeof m === 'object').map((member, idx) => (
              <div key={member.id || idx} className="card p-6">
                {editingMember && editingMember.id === member.id ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Headshot</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => editingMember && setEditingMember({ ...editingMember, newHeadshot: e.target.files[0] })}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editingMember?.name || ''}
                        onChange={(e) => editingMember && setEditingMember({ ...editingMember, name: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={editingMember?.title || ''}
                        onChange={(e) => editingMember && setEditingMember({ ...editingMember, title: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                      <textarea
                        value={editingMember?.bio || ''}
                        onChange={(e) => editingMember && setEditingMember({ ...editingMember, bio: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        rows="3"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleUpdateTeamMember(member.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingMember(null)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {member.headshotUrl ? (
                      <img
                        src={member.headshotUrl}
                        alt={member.name || 'Team member'}
                        className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                      />
                    ) : (
                      <div className="w-32 h-32 bg-blue-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                        <span className="text-blue-600 font-bold text-2xl">
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 text-center">{member.name || 'Unknown'}</h3>
                    <p className="text-sm text-gray-600 text-center mb-2">{member.title || ''}</p>
                    {member.bio && (
                      <button
                        onClick={() => setBioPopupMember(member)}
                        className="text-sm text-blue-600 hover:text-blue-700 hover:underline cursor-pointer mt-1"
                      >
                        View Bio
                      </button>
                    )}
                    {canEdit && (
                      <div className="mt-4 flex space-x-2">
                        <button
                          onClick={() => setEditingMember(member)}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTeamMember(member.id)}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex-1"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {investorTeam.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No team members added yet</p>
            </div>
          )}
        </div>
      )}

      {/* NDA Access Tab */}
      {activeTab === 'nda-access' && (
        <div>
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Investor NDA Records</h2>
            <p className="text-sm text-gray-600 mt-1">Investors who have signed NDAs to access confidential documents.</p>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by investor name, email, or company..."
              value={ndaSearch}
              onChange={(e) => setNdaSearch(e.target.value)}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {ndaLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-3 text-sm text-gray-600">Loading NDA records...</p>
              </div>
            </div>
          ) : ndaRecords.length > 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Signed Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {ndaRecords
                      .filter(record => {
                        if (!ndaSearch) return true;
                        const search = ndaSearch.toLowerCase();
                        return (
                          (record.investorEmail || '').toLowerCase().includes(search) ||
                          (record.investorName || '').toLowerCase().includes(search) ||
                          (record.investorCompany || '').toLowerCase().includes(search)
                        );
                      })
                      .map((record, index) => (
                      <tr key={record.id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {record.investorName || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {record.investorEmail || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.investorCompany || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.signedAt ? new Date(record.signedAt).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-500">No investors have signed NDAs yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Bio Popup Modal */}
      {bioPopupMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{bioPopupMember.name}</h2>
                {bioPopupMember.title && (
                  <p className="text-sm text-gray-600 mt-1">{bioPopupMember.title}</p>
                )}
              </div>
              <button
                onClick={() => setBioPopupMember(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{bioPopupMember.bio}</p>
          </div>
        </div>
      )}

      {/* Add Team Member Modal */}
      {showAddMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Add Team Member</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Headshot</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewMember({ ...newMember, headshot: e.target.files[0] })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newMember.name}
                  onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newMember.title}
                  onChange={(e) => setNewMember({ ...newMember, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                <textarea
                  value={newMember.bio}
                  onChange={(e) => setNewMember({ ...newMember, bio: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows="3"
                  placeholder="Short bio about this team member"
                />
              </div>
              <div className="flex space-x-2 pt-4">
                <button onClick={handleAddTeamMember} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1">
                  Add Member
                </button>
                <button
                  onClick={() => {
                    setShowAddMember(false);
                    setNewMember({ name: '', title: '', bio: '', headshot: null });
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Company Overview blurb editor (Investor Portal)
// ---------------------------------------------------------------------------
const CompanyOverviewEditor = ({ canEdit }) => {
  const [overview, setOverview] = useState('');
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/overview`);
        if (r.ok) {
          const d = await r.json();
          setOverview(d.overview || '');
        }
      } catch (e) { console.error('Error loading overview:', e); }
    })();
  }, []);

  const wordCount = (s) => (s || '').trim().split(/\s+/).filter(Boolean).length;
  const draftWords = wordCount(draft);

  const save = async () => {
    if (draftWords > 100) { alert('Overview must be 100 words or fewer.'); return; }
    setSaving(true);
    try {
      const r = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/overview`, { overview: draft });
      if (r.ok) {
        const d = await r.json();
        setOverview(d.overview ?? draft);
        setEditing(false);
      } else {
        alert('Failed to save overview.');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to save overview.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card p-4 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Company Overview</h2>
        {canEdit && !editing && (
          <button
            onClick={() => { setDraft(overview); setEditing(true); }}
            className="text-sm text-blue-600 hover:underline"
          >
            {overview ? 'Edit' : 'Add overview'}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full border border-gray-300 rounded p-2 text-sm"
            rows={5}
            maxLength={2000}
            placeholder="Briefly describe your company (max 100 words)..."
          />
          <div className="flex items-center justify-between mt-2">
            <span className={`text-xs ${draftWords > 100 ? 'text-red-600' : 'text-gray-500'}`}>
              {draftWords} / 100 words
            </span>
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300" disabled={saving}>
                Cancel
              </button>
              <button onClick={save} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50" disabled={saving || draftWords > 100}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : overview ? (
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{overview}</p>
      ) : (
        <p className="text-sm text-gray-500 italic">No company overview yet. This text appears below the title on the investor company view.</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Intellectual Property tab — list + add/edit/delete patents
// ---------------------------------------------------------------------------
const IPTab = () => {
  const [patents, setPatents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const emptyRow = { country: '', appType: '', patentStatus: '', serialNo: '', fileDate: '', patentNo: '', issueDate: '' };
  const empty = { title: '', ...emptyRow, filings: [] };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/patents`);
      const d = await r.json();
      setPatents(d.patents || []);
    } catch (e) { console.error('Error loading patents:', e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title) { alert('Patent title is required.'); return; }
    try {
      const r = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/patents`, editing);
      if (r.ok) { setEditing(null); load(); } else { alert('Failed to save patent.'); }
    } catch (e) { console.error(e); alert('Failed to save patent.'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this patent?')) return;
    try {
      const r = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/patents/${id}`);
      if (r.ok) load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Intellectual Property / Patents</h2>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">+ Add Patent</button>
      </div>
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : patents.length === 0 ? (
        <p className="text-gray-500 italic">No patents recorded yet.</p>
      ) : (
        <div className="space-y-6">
          {patents.map((p) => {
            const rows = [
              { country: p.country, appType: p.appType, patentStatus: p.patentStatus, serialNo: p.serialNo, fileDate: p.fileDate, patentNo: p.patentNo, issueDate: p.issueDate },
              ...(Array.isArray(p.filings) ? p.filings : [])
            ];
            return (
            <div key={p.id} className="border rounded-lg overflow-hidden">
              <div className="flex justify-between items-center bg-gray-50 px-4 py-2 border-b">
                <h3 className="font-semibold text-gray-900">{p.title}</h3>
                <div className="flex gap-2">
                  <button onClick={() => setEditing({ ...p, filings: Array.isArray(p.filings) ? p.filings : [] })} className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300">Add/Edit</button>
                  <button onClick={() => remove(p.id)} className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                </div>
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Country</th>
                    <th className="px-3 py-2 text-left">App Type</th>
                    <th className="px-3 py-2 text-left">Patent Status</th>
                    <th className="px-3 py-2 text-left">Serial No</th>
                    <th className="px-3 py-2 text-left">File Date</th>
                    <th className="px-3 py-2 text-left">Patent No</th>
                    <th className="px-3 py-2 text-left">Issue Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={idx} className={idx > 0 ? 'border-t' : ''}>
                      <td className="px-3 py-2">{row.country || '—'}</td>
                      <td className="px-3 py-2">{row.appType || '—'}</td>
                      <td className="px-3 py-2">{row.patentStatus || '—'}</td>
                      <td className="px-3 py-2">{row.serialNo || '—'}</td>
                      <td className="px-3 py-2">{row.fileDate || '—'}</td>
                      <td className="px-3 py-2">{row.patentNo || '—'}</td>
                      <td className="px-3 py-2">{row.issueDate || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })}
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editing.id ? 'Edit Patent' : 'New Patent'}</h3>
            <div className="mb-4">
              <Field label="Patent Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} colSpan={2} />
            </div>
            <div className="border rounded p-3 mb-3">
              <p className="text-xs font-semibold text-gray-700 mb-2">Filing #1</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Country" value={editing.country} onChange={(v) => setEditing({ ...editing, country: v })} />
                <Field label="App Type" value={editing.appType} onChange={(v) => setEditing({ ...editing, appType: v })} />
                <Field label="Patent Status" value={editing.patentStatus} onChange={(v) => setEditing({ ...editing, patentStatus: v })} />
                <Field label="Serial No" value={editing.serialNo} onChange={(v) => setEditing({ ...editing, serialNo: v })} />
                <Field label="File Date" type="date" value={editing.fileDate} onChange={(v) => setEditing({ ...editing, fileDate: v })} />
                <Field label="Patent No" value={editing.patentNo} onChange={(v) => setEditing({ ...editing, patentNo: v })} />
                <Field label="Issue Date" type="date" value={editing.issueDate} onChange={(v) => setEditing({ ...editing, issueDate: v })} />
              </div>
            </div>
            {(editing.filings || []).map((row, idx) => (
              <div key={idx} className="border rounded p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700">Filing #{idx + 2}</p>
                  <button
                    onClick={() => {
                      const next = [...(editing.filings || [])];
                      next.splice(idx, 1);
                      setEditing({ ...editing, filings: next });
                    }}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {['country','appType','patentStatus','serialNo','fileDate','patentNo','issueDate'].map((key) => {
                    const labels = { country: 'Country', appType: 'App Type', patentStatus: 'Patent Status', serialNo: 'Serial No', fileDate: 'File Date', patentNo: 'Patent No', issueDate: 'Issue Date' };
                    const type = (key === 'fileDate' || key === 'issueDate') ? 'date' : 'text';
                    return (
                      <Field
                        key={key}
                        label={labels[key]}
                        type={type}
                        value={row[key]}
                        onChange={(v) => {
                          const next = [...(editing.filings || [])];
                          next[idx] = { ...next[idx], [key]: v };
                          setEditing({ ...editing, filings: next });
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            <button
              onClick={() => setEditing({ ...editing, filings: [...(editing.filings || []), { ...emptyRow }] })}
              className="mb-4 text-sm text-blue-600 hover:underline"
            >
              + Add another filing row
            </button>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Funding tab — list + add/edit/delete funding entries
// ---------------------------------------------------------------------------
const FundingTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const empty = { dateAwarded: '', totalAmountAwarded: '', grantingAgency: '', grantType: '', grantTypeOther: '' };
  const grantTypes = ['SBIR/STTR', 'PE', 'VC', 'Angel', 'Federal/State Grant', 'Other'];

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/funding`);
      const d = await r.json();
      setItems(d.funding || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.dateAwarded || !editing.totalAmountAwarded || !editing.grantingAgency || !editing.grantType) {
      alert('Please fill in all required fields.');
      return;
    }
    if (editing.grantType === 'Other' && !editing.grantTypeOther) {
      alert('Please specify the "Other" grant type.');
      return;
    }
    try {
      const r = await api.post(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/funding`, editing);
      if (r.ok) { setEditing(null); load(); } else { alert('Failed to save funding entry.'); }
    } catch (e) { console.error(e); alert('Failed to save.'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this funding entry?')) return;
    try {
      const r = await api.delete(`${process.env.REACT_APP_BACKEND_API_URL}/investor-portal/funding/${id}`);
      if (r.ok) load();
    } catch (e) { console.error(e); }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Funding</h2>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">+ Add Funding</button>
      </div>
      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 italic">No funding entries yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Date Awarded</th>
                <th className="px-3 py-2 text-left">Total Amount</th>
                <th className="px-3 py-2 text-left">Granting Agency</th>
                <th className="px-3 py-2 text-left">Grant Type</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-t">
                  <td className="px-3 py-2">{f.dateAwarded}</td>
                  <td className="px-3 py-2">{f.totalAmountAwarded}</td>
                  <td className="px-3 py-2">{f.grantingAgency}</td>
                  <td className="px-3 py-2">{f.grantType === 'Other' ? `Other: ${f.grantTypeOther || ''}` : f.grantType}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing({ ...f })} className="text-xs px-2 py-1 bg-gray-200 rounded hover:bg-gray-300 mr-2">Edit</button>
                    <button onClick={() => remove(f.id)} className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">{editing.id ? 'Edit Funding' : 'New Funding'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Date Awarded *" type="date" value={editing.dateAwarded} onChange={(v) => setEditing({ ...editing, dateAwarded: v })} />
              <Field label="Total Amount Awarded *" value={editing.totalAmountAwarded} onChange={(v) => setEditing({ ...editing, totalAmountAwarded: v })} />
              <Field label="Granting Agency *" value={editing.grantingAgency} onChange={(v) => setEditing({ ...editing, grantingAgency: v })} colSpan={2} />
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Grant Type *</label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={editing.grantType}
                  onChange={(e) => setEditing({ ...editing, grantType: e.target.value })}
                >
                  <option value="">Select...</option>
                  {grantTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {editing.grantType === 'Other' && (
                <Field label="Specify Other *" value={editing.grantTypeOther} onChange={(v) => setEditing({ ...editing, grantTypeOther: v })} colSpan={2} />
              )}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', colSpan = 1 }) => (
  <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
    />
  </div>
);

export default DataRoom;
